import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { subscriptionAPI } from '../../../utils/api';
import { handleIOSSubscription, isIOS } from '../../../utils/iapPayment';
import appleIAP, { PRODUCT_IDS, APP_STORE_FALLBACK_PRICES } from '../../../services/appleIAP';
import PaymentGateway from '../../../components/PaymentGateway';
import AppleSubscriptionDisclosure from '../../../components/AppleSubscriptionDisclosure';

const PatientSubscription = () => {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [appleProducts, setAppleProducts] = useState({});
  const [iapFinished, setIapFinished] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const iosDevice = isIOS();

  useEffect(() => {
    fetchSubscriptionPlans();
    if (iosDevice) {
      let cleanup;
      initIAP().then(fn => { cleanup = fn; });
      return () => { cleanup?.(); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAppleProducts = () => {
    const productIds = Object.values(PRODUCT_IDS.patient);
    plans.forEach(p => {
      const id = getProductId(p);
      if (id && !productIds.includes(id)) productIds.push(id);
    });

    const prods = {};
    for (const id of productIds) {
      const p = appleIAP.getProduct(id);
      if (p) prods[id] = p;
    }
    if (Object.keys(prods).length > 0) setAppleProducts(prods);
  };

  const initIAP = async () => {
    try {
      await appleIAP.initialize();
      loadAppleProducts();

      const removeListener = appleIAP.on('products-updated', () => {
        loadAppleProducts();
      });

      let attempts = 0;
      const pollInterval = setInterval(() => {
        attempts++;
        const ids = Object.values(PRODUCT_IDS.patient);
        const hasPrice = ids.some(id => appleIAP.getProduct(id)?.localizedPrice);
        if (hasPrice || attempts >= 10) {
          clearInterval(pollInterval);
          loadAppleProducts();
          setIapFinished(true);
          removeListener?.();
        } else {
          loadAppleProducts();
        }
      }, 1000);

      return () => {
        clearInterval(pollInterval);
        removeListener?.();
      };
    } catch (err) {
      console.error('IAP init error:', err);
      setIapFinished(true);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const res = await subscriptionAPI.getPlans('patient');
      if (res.data?.success && Array.isArray(res.data.data)) {
        const fetchedPlans = res.data.data;
        setPlans(fetchedPlans);
        
        const dynamicIds = fetchedPlans
          .map(p => getProductId(p))
          .filter(Boolean);
        if (dynamicIds.length > 0) {
          await appleIAP.registerDynamicProducts(dynamicIds);
          loadAppleProducts();
        }
      }
    } catch (error) {
      showError('Failed to fetch subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const getApplePrice = (plan) => {
    const productId = getProductId(plan);
    if (!productId) return null;
    if (appleProducts[productId]?.localizedPrice) {
      return appleProducts[productId].localizedPrice;
    }
    if (plan.ios_amount) {
      return `₹${plan.ios_amount}`;
    }
    if (plan.amount) {
      return `₹${plan.amount}`;
    }
    if (APP_STORE_FALLBACK_PRICES[productId]) {
      return APP_STORE_FALLBACK_PRICES[productId].display;
    }
    return null;
  };

  const getProductId = (plan) => {
    if (plan.appleProductId) return plan.appleProductId;
    const name = (plan.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (name.includes('platinum')) return PRODUCT_IDS.patient.platinum;
    if (name.includes('gold')) return PRODUCT_IDS.patient.gold;
    if (name.includes('premium')) return PRODUCT_IDS.patient.premium;
    if (name.includes('basic')) return PRODUCT_IDS.patient.basic;
    return null;
  };

  const subscribeToPlan = async (plan) => {
    const targetUserId = user?._id || user?.id;
    if (!targetUserId) {
      showError('Please log in to subscribe');
      return;
    }
    setPayingPlanId(plan._id);

    if (iosDevice) {
      const productId = getProductId(plan);
      if (!productId) {
        showError('Apple product not found for this plan');
        setPayingPlanId(null);
        return;
      }

      await handleIOSSubscription({
        plan: { ...plan, appleProductId: productId },
        role: 'patient',
        onSuccess: async () => {
          showSuccess('Subscription activated successfully!');
          setPayingPlanId(null);
          await refreshProfile();
          navigate('/patient/home');
        },
        onError: (msg) => {
          showError(msg);
          setPayingPlanId(null);
        },
        onFinally: () => setPayingPlanId(null),
      });
      return;
    }

    // For Android/Web, use Razorpay
    setSelectedPlan(plan);
    setShowPayment(true);
  };

  const handleRestorePurchases = async () => {
    try {
      await appleIAP.restorePurchases();
      showSuccess('Purchases restored!');
      await refreshProfile();
    } catch (err) {
      showError(err.message || 'Failed to restore purchases');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-10 py-8 px-4">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 via-yellow-400 to-amber-500">
          Unlock Premium Health
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Choose a plan that fits your needs and get exclusive access to premium consultations, chat features, and more.
        </p>
        <button
          onClick={handleRestorePurchases}
          className="mt-2 text-sm font-medium text-yellow-500/80 hover:text-yellow-500 transition-colors underline underline-offset-4"
        >
          Restore Purchases
        </button>
      </div>

      <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl p-6 border border-white/5 shadow-2xl backdrop-blur-xl flex justify-between items-center">
        <div>
          <p className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider">Current Status</p>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${user?.subscription_status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`}></div>
            <p className="text-xl font-bold text-white tracking-wide">
              {user?.subscription_status?.replace('_', ' ').toUpperCase() || 'INACTIVE'}
            </p>
          </div>
        </div>
        {user?.subscription_status === 'active' && user?.subscription_end_date && (
          <div className="text-right">
            <p className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider">Valid Until</p>
            <p className="text-xl font-bold text-white tracking-wide">
              {new Date(user.subscription_end_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {plans.length === 0 ? (
          <div className="col-span-full text-center py-16 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-md">
            <p className="text-xl text-gray-300">No subscription plans available yet.</p>
            <p className="text-sm text-gray-500 mt-3">Please check back later.</p>
          </div>
        ) : (
          plans.map((plan) => {
            const applePrice = getApplePrice(plan);
            return (
              <div 
                key={plan._id} 
                className="group relative bg-white/5 rounded-3xl p-8 border border-white/10 shadow-xl backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] hover:border-yellow-500/30 hover:shadow-yellow-500/10 overflow-hidden flex flex-col"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/10 rounded-full blur-3xl -mr-10 -mt-10 transition-opacity group-hover:opacity-100 opacity-0"></div>
                
                <div className="mb-8">
                  <h3 className="text-2xl font-black text-white mb-2">{plan.name}</h3>
                  {plan.description && (
                    <p className="text-gray-400 text-sm leading-relaxed min-h-[40px]">{plan.description}</p>
                  )}
                </div>

                <div className="mb-8">
                  <p className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-600 tracking-tight">
                    {iosDevice ? (applePrice || (plan?.amount ? `₹${Math.round(plan.amount * 1.18)}` : '')) : (plan?.amount ? `₹${Math.round(plan.amount * 1.18)}` : '')}
                  </p>
                  <p className="text-sm text-gray-500 mt-2 font-medium tracking-wide">
                    {iosDevice ? 'App Store Subscription' : '+ 18% GST Included'}
                  </p>
                </div>

                <div className="flex-grow space-y-4 mb-8">
                  <h4 className="text-sm font-bold text-gray-300 uppercase tracking-wider">Plan Includes</h4>
                  <ul className="space-y-3">
                    <li className="flex items-center text-gray-400">
                      <svg className="w-5 h-5 text-yellow-500 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                      <span className="font-medium text-gray-200">{plan.chat_limit ?? 0}</span>&nbsp;Chat Sessions
                    </li>
                    <li className="flex items-center text-gray-400">
                      <svg className="w-5 h-5 text-yellow-500 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                      <span className="font-medium text-gray-200">{plan.voice_limit ?? 0}</span>&nbsp;Voice Sessions
                    </li>
                    <li className="flex items-center text-gray-400">
                      <svg className="w-5 h-5 text-yellow-500 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                      <span className="font-medium text-gray-200">{plan.video_limit ?? 0}</span>&nbsp;Video Sessions
                    </li>
                    <li className="flex items-center text-gray-400">
                      <svg className="w-5 h-5 text-yellow-500 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      Valid for <span className="font-medium text-gray-200 ml-1">{plan.duration_days ?? 30} Days</span>
                    </li>
                  </ul>
                </div>

                {isIOS() ? (
                  <AppleSubscriptionDisclosure
                    planName={plan.name}
                    price={applePrice || (plan?.amount ? `₹${Math.round(plan.amount * 1.18)}` : "")}
                    durationDays={plan.duration_days ?? 30}
                  />
                ) : null}

                <button
                  onClick={() => subscribeToPlan(plan)}
                  disabled={user?.subscription_status === 'active' || payingPlanId === plan._id}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 mt-auto ${
                    user?.subscription_status === 'active'
                      ? 'bg-white/10 text-gray-400 cursor-not-allowed border border-white/5'
                      : payingPlanId === plan._id
                        ? 'bg-yellow-500/80 text-white cursor-wait animate-pulse'
                        : 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white hover:shadow-[0_0_20px_rgba(234,179,8,0.4)] hover:-translate-y-1'
                  }`}
                >
                  {user?.subscription_status === 'active'
                    ? 'Already Purchased'
                    : payingPlanId === plan._id
                      ? 'Processing...'
                      : iosDevice
                      ? `Subscribe for ${applePrice || ('₹' + Math.round(plan.amount * 1.18))}`
                      : `Subscribe for ₹${Math.round(plan.amount * 1.18)}`}
                </button>

              </div>
            );
          })
        )}
      </div>

      {showPayment && selectedPlan && (
        <PaymentGateway
          amount={selectedPlan.amount}
          planName={selectedPlan.name}
          userId={user?._id || user?.id}
          productId={getProductId(selectedPlan)}
          role="patient"
          onSuccess={async () => {
            showSuccess('Subscription activated successfully!');
            setShowPayment(false);
            setPayingPlanId(null);
            await refreshProfile();
            navigate('/patient/home');
          }}
          onError={(msg) => {
            showError(msg);
            setShowPayment(false);
            setPayingPlanId(null);
          }}
          onClose={() => {
            setShowPayment(false);
            setPayingPlanId(null);
          }}
        />
      )}
    </div>
  );
};

export default PatientSubscription;
